/**
 * Onboarding API - save and retrieve onboarding step statuses.
 * Base URL: set VITE_API_BASE_URL in .env
 *
 * Endpoints:
 *   GET  /bot/api/shopify-onboarding        → retrieve shopify setup status
 *   POST /bot/api/shopify-onboarding        → update shopify setup status
 *   POST /bot/api/shopify/activate-widget   → activate widget on store theme
 */

import { getStoreInfo } from './shopify/sync.js';
import { getAuthHeaders, getStoredUser } from './auth';
import { getBaseUrl, getShopifyApiBaseUrl } from './utils.js';

/**
 * Mapping between step keys and API field names
 * Maps to ONBOARDING_FIELDS: pageblog, settings, testai, widget, active
 */
const STEP_KEY_TO_API_FIELD = {
  'sync-content': 'pageblog',
  'review-settings': 'settings',
  'test-bot': 'testai',
  'customize-widget': 'widget',
  'activate-widget': 'active',
};

/**
 * Create default onboarding steps with dynamic data from store info API
 * @param {object} storeInfo - Store info from getStoreInfo() API
 * @returns {Array} Array of step objects with semantic keys
 */
const createDefaultSteps = (storeInfo = null) => {
  return [
    {
      id: 1,
      key: 'shopify-connected',
      title: 'Your Shopify store data is being synchronized',
      description: '',
      status: 'completed', // Marked as completed by default
      type: 'required',
      action: 'View Products',
      capabilityLine: 'AeroChat can now answer your customers about products, recommendations, order/delivery status, discounts and payments.',
    },
    {
      id: 2,
      key: 'sync-content',
      title: 'Your Shopify store pages and articles are being synchronized',
      description: 'Import additional content to help AI answer customer questions',
      status: 'completed', // Completed by default
      type: 'optional',
      action: 'View Pages & Articles',
    },
    {
      id: 3,
      key: 'review-settings',
      title: 'Review Business Settings',
      description: 'Verify your business hours, timezone, currency, and language',
      status: 'pending',
      type: 'required',
      action: 'Review Settings',
    },
    {
      id: 4,
      key: 'test-bot',
      title: 'Test Drive Your AI Assistant',
      description: 'Chat with your AI to see how it handles customer questions',
      status: 'pending',
      type: 'required',
      action: 'Test Now',
    },
    {
      id: 5,
      key: 'customize-widget',
      title: 'Customize Widget Appearance',
      description: 'Add your logo, brand colors, and welcome message',
      status: 'pending',
      type: 'recommended',
      action: 'Customize Widget',
    },
    {
      id: 6,
      key: 'activate-widget',
      title: 'Activate AeroChat on Your Shopify Store',
      description: 'A new window will open with your Shopify theme editor. The AeroChat widget is already enabled — just click Save in the theme editor to complete activation.',
      status: 'pending',
      type: 'required',
      action: 'Activate Now',
    },
  ];
};

/**
 * Fetch all onboarding step statuses.
 * GET /bot/api/shopify-onboarding
 *
 * Expected response:
 * {
 *   "pageblog": 0|1|3,  // 0=pending, 1=completed, 3=skipped
 *   "settings": 0|1|3,
 *   "testai": 0|1|3,
 *   "widget": 0|1|3,
 *   "active": 0|1|3,
 *   "all_completed": boolean
 * }
 *
 * @returns {Promise<Array>} array of step objects with semantic keys
 */
export async function getOnboardingSteps() {
  const base = getBaseUrl();
  const setupUrl = `${base}/bot/api/shopify-onboarding`.replace(/([^:]\/)\/+/g, '$1');

  // Fetch both APIs in parallel
  const [storeInfoResult, setupResult] = await Promise.allSettled([
    getStoreInfo(),
    fetch(`${setupUrl}?_t=${Date.now()}`, { method: 'GET', headers: { Accept: 'application/json', 'Cache-Control': 'no-cache', ...getAuthHeaders() } })
      .then(res => {
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.error) throw new Error(data.error);
        return data;
      }),
  ]);

  const storeInfo = storeInfoResult.status === 'fulfilled' ? storeInfoResult.value : null;
  const shopifySetup = setupResult.status === 'fulfilled' ? setupResult.value : null;

  if (storeInfoResult.status === 'rejected') {
    console.warn('Store info API failed, using defaults:', storeInfoResult.reason);
  }
  if (setupResult.status === 'rejected') {
    console.warn('Shopify setup API failed, using defaults:', setupResult.reason);
  }

  // Create steps with store info counts, then overlay statuses from setup API
  const steps = createDefaultSteps(storeInfo);

  if (!shopifySetup) return steps;

  return steps.map((step) => {
    const apiField = STEP_KEY_TO_API_FIELD[step.key];
    if (apiField && shopifySetup[apiField] !== undefined) {
      const apiValue = shopifySetup[apiField];
      // Map API integer values to status: 1=completed, 2=visited, 3=skipped, 0 or other=pending
      if (apiValue === 1) {
        return { ...step, status: 'completed' };
      } else if (apiValue === 2) {
        return { ...step, status: 'visited' };
      } else if (apiValue === 3) {
        return { ...step, status: 'skipped' };
      }
      // apiValue === 0 or other means pending
    }
    return step;
  });
}

/**
 * Update the status of a single onboarding step.
 * POST /bot/api/shopify-onboarding
 *
 * Request body:
 * {
 *   "pageblog": 0|1|3,  // 0=pending, 1=completed, 3=skipped
 *   "settings": 0|1|3,
 *   "testai": 0|1|3,
 *   "widget": 0|1|3,
 *   "active": 0|1|3
 * }
 *
 * Expected response:
 * {
 *   "updated_fields": ["pageblog"]
 * }
 *
 * @param {number|string} stepIdOrKey - the step ID or semantic key to update
 * @param {string} status - new status: 'pending', 'completed', or 'skipped'
 * @returns {Promise<object>}
 */
export async function updateStepStatus(stepIdOrKey, status) {
  try {
    // Convert step key to API field name
    let apiField = null;
    
    if (typeof stepIdOrKey === 'string' && STEP_KEY_TO_API_FIELD[stepIdOrKey]) {
      apiField = STEP_KEY_TO_API_FIELD[stepIdOrKey];
    } else {
      // Try to find by ID (steps 2-6 map to API fields, step 1 doesn't)
      const stepId = typeof stepIdOrKey === 'number' ? stepIdOrKey : parseInt(stepIdOrKey);
      const stepKeys = ['sync-content', 'review-settings', 'test-bot', 'customize-widget', 'activate-widget'];
      if (stepId >= 2 && stepId <= 6) {
        const stepKey = stepKeys[stepId - 2];
        apiField = STEP_KEY_TO_API_FIELD[stepKey];
      }
    }

    // If not a valid API field (step 1 only), skip API call to avoid unnecessary requests
    if (!apiField) {
      console.warn(`Step ${stepIdOrKey} does not map to API field, skipping API update`);
      return { updated_fields: [] };
    }

    // Convert status to integer: 1=completed, 2=visited, 3=skipped, 0=pending
    let apiValue = 0; // default to pending
    if (status === 'completed') {
      apiValue = 1;
    } else if (status === 'visited') {
      apiValue = 2;
    } else if (status === 'skipped') {
      apiValue = 3;
    }

    const base = getBaseUrl();
    const url = `${base}/bot/api/shopify-onboarding`.replace(/([^:]\/)\/+/g, '$1');
    
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        [apiField]: apiValue,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `API returned ${res.status}`);
    }

    return await res.json();
  } catch (err) {
    console.warn('Failed to save step status:', err);
    // Don't throw - UI already updated optimistically
    return null;
  }
}

/**
 * Get the app embed URL for enabling the chatbot in Shopify theme.
 * GET /api/app_embed_url?company_id={companyId}
 *
 * Expected response:
 * {
 *   "data": {
 *     "app_embed_url": "https://admin.shopify.com/store/...",
 *     "company_id": "9",
 *     "redirect_url": "https://admin.shopify.com/store/...",
 *     "shop_domain": "example.myshopify.com",
 *     "store_handle": "example"
 *   },
 *   "status": "success"
 * }
 *
 * @param {number|string} [companyId] - Company ID (defaults to stored user's company_id)
 * @returns {Promise<{ redirect_url: string, app_embed_url: string, ... }>}
 */
export async function getAppEmbedUrl(companyId = null) {
  try {
    if (companyId == null) {
      const user = getStoredUser();
      companyId = user?.company_id ?? null;
    }
    const base = getShopifyApiBaseUrl();
    const url = `${base}/api/app_embed_url?company_id=${companyId}`;
    
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `API returned ${res.status}`);
    }

    const data = await res.json();
    
    if (data.status !== 'success' || !data.data) {
      throw new Error('Invalid API response format');
    }

    return data.data;
  } catch (err) {
    console.error('Failed to get app embed URL:', err);
    throw err;
  }
}

/**
 * Check the widget embed status on the Shopify store's active theme.
 * GET /bot/api/shopify/widget-status
 *
 * Read-only check via Shopify GraphQL — does not modify anything.
 *
 * @returns {Promise<{ success: boolean, status: "ENABLED" | "DISABLED" | "NOT_PRESENT" }>}
 */
export async function getWidgetStatus() {
  const base = getBaseUrl();
  const url = `${base}/bot/api/shopify/widget-status`.replace(/([^:]\/)\/+/g, '$1');

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
  });

  const data = await res.json();

  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Failed to check widget status');
  }

  return data;
}
